import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AccountHolder,
  AccountHolderRatePlan,
  updateRatePlan,
  UpdateRatePlanBody,
} from "../../../services/accountService";
import { formatAmount } from "../../../utils/currency";
import useCreateAccountHolderNotesMutation from "./useCreateAccountHolderNotesMutation";

function checkRatePlanFieldsAreDifferent(
  previousField: string | undefined,
  currentField: string | undefined,
  label: string
) {
  if (
    previousField !== undefined &&
    currentField !== undefined &&
    previousField !== currentField
  ) {
    return `${label} (${formatAmount(+previousField)} to ${formatAmount(
      +currentField
    )})`;
  }
  return "";
}

export function getRatePlanChanges(
  previous: Partial<AccountHolderRatePlan>,
  current: Partial<AccountHolderRatePlan>
): string {
  const changes: string[] = [];
  // Flat Rate changes
  changes.push(
    checkRatePlanFieldsAreDifferent(
      previous.percentageValue,
      current.percentageValue,
      "Online Percentage"
    )
  );

  changes.push(
    checkRatePlanFieldsAreDifferent(
      previous.fixedAmount,
      current.fixedAmount,
      "Online Transaction"
    )
  );

  changes.push(
    checkRatePlanFieldsAreDifferent(
      previous.ippPercentage,
      current.ippPercentage,
      "In Person Percentage"
    )
  );

  changes.push(
    checkRatePlanFieldsAreDifferent(
      previous.ippFixedAmount,
      current.ippFixedAmount,
      "In Person Transaction"
    )
  );

  // Interchange++ changes
  changes.push(
    checkRatePlanFieldsAreDifferent(
      previous.icPercentage,
      current.icPercentage,
      "Online Markup"
    )
  );

  changes.push(
    checkRatePlanFieldsAreDifferent(
      previous.icFixedAmount,
      current.icFixedAmount,
      "Online Transaction"
    )
  );

  changes.push(
    checkRatePlanFieldsAreDifferent(
      previous.icIppPercentage,
      current.icIppPercentage,
      "In Person Markup"
    )
  );

  changes.push(
    checkRatePlanFieldsAreDifferent(
      previous.icIppFixedAmount,
      current.icIppFixedAmount,
      "In Person Transaction"
    )
  );

  if (previous.allowedMethod !== current.allowedMethod) {
    changes.push(
      `Allowed (${previous.allowedMethod ? "yes" : "no"} to ${
        current.allowedMethod ? "yes" : "no"
      })`
    );
  }

  if (previous.surcharge !== current.surcharge) {
    changes.push(
      `Surcharge (${previous.surcharge ? "yes" : "no"} to ${
        current.surcharge ? "yes" : "no"
      })`
    );
  }

  return `Field rate plan ${previous?.paymentMethodType?.toUpperCase()} changed: ${changes
    .filter((x) => x !== "")
    .join(", ")}`;
}

export default function useUpdateRatePlanMutation(accountHolderCode: string) {
  const queryClient = useQueryClient();
  const { mutateAsync: createNote } = useCreateAccountHolderNotesMutation();

  return useMutation<string, Error, UpdateRatePlanBody>(
    (body) => updateRatePlan(body),
    {
      async onSuccess(_, updatedRatePlan) {
        const accountConfigData = queryClient.getQueryData<AccountHolder>([
          "accountConfig",
          accountHolderCode,
        ]);

        const updatedRatePlans = accountConfigData?.ratePlans.some(
          (ratePlan) =>
            ratePlan.paymentMethodType === updatedRatePlan.paymentMethodType
        )
          ? accountConfigData?.ratePlans.map((ratePlan) => {
              return ratePlan.paymentMethodType ===
                updatedRatePlan.paymentMethodType
                ? updatedRatePlan
                : ratePlan;
            })
          : [...(accountConfigData?.ratePlans || []), updatedRatePlan];

        queryClient.setQueryData(["accountConfig", accountHolderCode], {
          ...accountConfigData,
          ratePlans: updatedRatePlans,
        });

        const previousUpdatedRatePlan = accountConfigData?.ratePlans?.find(
          (rp) => rp.paymentMethodType === updatedRatePlan.paymentMethodType
        );

        if (previousUpdatedRatePlan && updatedRatePlan) {
          await createNote({
            accountHolderCode,
            freeText: getRatePlanChanges(
              previousUpdatedRatePlan,
              updatedRatePlan
            ),
          });
        }

        queryClient.invalidateQueries(["accountConfig", accountHolderCode]);
      },
    }
  );
}
